import dynamic from 'next/dynamic';

import BuilderPixel, { type BuilderPixelProps } from 'components/Pixel';

import { hasArrayValues } from 'utils/array/arrays';

import type { BuilderElement } from '@builder.io/sdk';
import type { FC } from 'react';

interface ComponentGeneratorProps {
  components?: BuilderElement[];
  data?: Record<string, unknown>;
}

// This is the VISITOR rendering path. RenderBuilderContent sends previewing
// clients (the Builder editor, preview links) to <BuilderComponent>, which
// resolves blocks through the registrations in utils/builder/ComponentGenerator,
// and everyone else here. A component registered only there renders correctly
// in the editor and then silently resolves to null for published traffic, since
// the default case below only warns. Every Builder component therefore needs an
// entry in BOTH files, and the `case` string must match the config's `name`
// exactly.

const Accordion = dynamic(() => import('components/Accordion'));
const Article = dynamic(() => import('layout/article'));
const ArticleAccordion = dynamic(() => import('components/ArticleAccordion'));
const Awards = dynamic(() => import('components/Awards'));
const BuildVsBuyQuiz = dynamic(() => import('components/BuildVsBuyQuiz'));
const FreeTrialFTUX = dynamic(() => import('components/FreeTrialFTUX'));
const ButtonSelector = dynamic(() => import('components/ButtonSelector'));
const BentoBox = dynamic(() => import('components/CardDeck/BentoBox'));
const CardCarousel = dynamic(() => import('components/CardCarousel'));
const CallOut = dynamic(() => import('organisms/Cards/CallOut'));
const CapabilityCallout = dynamic(() => import('components/CapabilityCallout'));
const Calculator = dynamic(() => import('components/Calculator'));
const CodeBlock = dynamic(() => import('molecules/CodeBlock'));
const Company = dynamic(() => import('organisms/Cards/Company'));
const ConnectHero = dynamic(() => import('components/ConnectHero'));
const ConnectMasthead = dynamic(() => import('components/ConnectMasthead'));
const ConnectRegistration = dynamic(() => import('components/ConnectRegistration'));
const Comparison = dynamic(() => import('components/Tables/Comparison'));
const ContainedConversionPanel = dynamic(() => import('components/ContainedConversionPanel'));
const ConversionHero = dynamic(() => import('components/ConversionHero'));
const ConversionPanel = dynamic(() => import('components/ConversionPanel'));
const ComparisonCards = dynamic(() => import('components/ComparisonCards'));
const DualFeatureCards = dynamic(() => import('components/DualFeatureCards'));
const EventExperience = dynamic(() => import('components/EventExperience'));
const EventOverview = dynamic(() => import('components/EventOverview'));
const EventRegistrationHero = dynamic(() => import('components/EventRegistrationHero'));
const FeatureComparisonTable = dynamic(() => import('components/Tables/FeatureComparisonTable'));
const FeatureSpotlight = dynamic(() => import('components/FeatureSpotlight'));
const FeaturedPartnersCards = dynamic(() => import('components/FeaturedPartners'));
const FeaturedSessions = dynamic(() => import('components/FeaturedSessions'));
const Heading = dynamic(() => import('components/Heading'));
const Hero = dynamic(() => import('components/Hero'));
const CustomCard = dynamic(() => import('components/CustomCard'));
const DashboardBackdrop = dynamic(() => import('components/DashboardBackdrop'));
const IFrame = dynamic(() => import('components/IFrame'));
const Icon = dynamic(() => import('components/CardDeck/Icon'));
const ImageCardDeckComponent = dynamic(() => import('components/CardDeck/Image'));
const JumpNav = dynamic(() => import('components/JumpNav'));
const LandingPageHero = dynamic(() => import('components/LandingPageHero'));
const Listing = dynamic(() => import('layout/Listing'));
const MeetTheSpeakers = dynamic(() => import('components/MeetTheSpeakers'));
const Metrics = dynamic(() => import('components/Metrics'));
const ProductExtensionColumns = dynamic(() => import('components/ProductExtensionColumns'));
const PricingV1 = dynamic(() => import('components/Tables/PricingV1'));
const PricingV2 = dynamic(() => import('components/Tables/PricingV2'));
const Quote = dynamic(() => import('organisms/Cards/Quote'));
const Resource = dynamic(() => import('components/CardDeck/Resource'));
const ResourceConversionPanel = dynamic(() => import('organisms/Cards/ResourceConversionPanel'));
const ResourceDownload = dynamic(() => import('organisms/Cards/ResourceDownload'));
const RichText = dynamic(() => import('molecules/RichText'));
const RichTextImage = dynamic(() => import('molecules/Image/RichTextImage'));
const Simple = dynamic(() => import('components/CardDeck/Simple'));
const Slidebox = dynamic(() => import('components/Slidebox'));
const SpeakerCards = dynamic(() => import('components/SpeakerCards'));
const SocialShare = dynamic(() => import('components/SocialShare'));
const Stats = dynamic(() => import('organisms/Cards/Stats'));
const StatsModule = dynamic(() => import('components/StatsModule'));
const SplitContentSection = dynamic(() => import('components/SplitContentSection'));
const Switchback = dynamic(() => import('components/Switchback'));
const Testimonials = dynamic(() => import('components/Testimonials'));
const Transition = dynamic(() => import('organisms/Transition'));
const Trustbar = dynamic(() => import('components/Trustbar'));
const Video = dynamic(() => import('molecules/Video'));
const VideoCustom = dynamic(() => import('molecules/VideoCustom'));
const WistiaForm = dynamic(() => import('components/WistiaForm'));
const WistiaSplit = dynamic(() => import('components/WistiaSplit'));

const getComponent = (
  { id, component, children, ...props }: BuilderElement,
  data?: Record<string, unknown>,
): JSX.Element | null => {
  if (id?.includes('builder-pixel')) {
    return <BuilderPixel {...(props as unknown as BuilderPixelProps)} />;
  }

  if (!component?.name) {
    return null;
  }

  switch (component?.name) {
    case 'accordion':
      return <Accordion key={id} {...component.options} />;
    case 'articleAccordion':
      return <ArticleAccordion key={id} {...component.options} />;
    case 'Article':
      return (
        <Article key={id} {...component.options} builderState={{ state: data }}>
          {hasArrayValues(children) && children.map((child: BuilderElement) => getComponent(child))}
        </Article>
      );
    case 'ButtonSelector':
      return <ButtonSelector key={id} {...component.options} />;
    case 'awards':
      return <Awards key={id} {...component.options} />;
    case 'BentoBoxCardDeck':
      return <BentoBox key={id} {...component.options} />;
    case 'BuildVsBuyQuiz':
      return <BuildVsBuyQuiz key={id} {...component.options} />;
    case 'FreeTrialFTUX':
      return <FreeTrialFTUX key={id} {...component.options} />;
    case 'CardCarousel':
      return <CardCarousel key={id} {...component.options} />;
    case 'callOutCard':
      return <CallOut key={id} {...component.options} />;
    case 'CapabilityCallout':
      return <CapabilityCallout key={id} {...component.options} />;
    case 'Calculator':
      return <Calculator key={id} {...component.options} />;
    case 'codeBlock':
      return <CodeBlock key={id} {...component.options} />;
    case 'Company Card':
      return <Company key={id} {...component.options} />;
    case 'ConnectHero':
      return <ConnectHero key={id} {...component.options} />;
    case 'ConnectMasthead':
      return <ConnectMasthead key={id} {...component.options} />;
    case 'ConnectRegistration':
      return <ConnectRegistration key={id} {...component.options} />;
    case 'Comparison Table':
      return <Comparison key={id} {...component.options} />;
    case 'Contained Conversion Panel':
      return <ContainedConversionPanel key={id} {...component.options} />;
    case 'ConversionHero':
      return <ConversionHero key={id} {...component.options} />;
    case 'Conversion Panel':
      return <ConversionPanel key={id} {...component.options} />;
    case 'ComparisonCards':
      return <ComparisonCards key={id} {...component.options} />;
    case 'DualFeatureCards':
      return <DualFeatureCards key={id} {...component.options} />;
    case 'ProductExtensionColumns':
      return <ProductExtensionColumns key={id} {...component.options} />;
    case 'EventExperience':
      return <EventExperience key={id} {...component.options} />;
    case 'EventOverview':
      return <EventOverview key={id} {...component.options} />;
    case 'EventRegistrationHero':
      return <EventRegistrationHero key={id} {...component.options} />;
    case 'Feature Comparison Table':
      return <FeatureComparisonTable key={id} {...component.options} />;
    case 'FeatureSpotlight':
      return <FeatureSpotlight key={id} {...component.options} />;
    case 'featuredPartnersCards':
      return <FeaturedPartnersCards key={id} {...component.options} />;
    case 'FeaturedSessions':
      return <FeaturedSessions key={id} {...component.options} />;
    case 'heading':
      return <Heading key={id} {...component.options} />;
    case 'Hero':
      return <Hero key={id} {...component.options} />;
    case 'IconCardDeck':
      return <Icon key={id} {...component.options} />;
    case 'ImageCardDeck':
      return <ImageCardDeckComponent key={id} {...component.options} />;
    case 'Custom Card':
      return <CustomCard key={id} {...component.options} />;
    case 'DashboardBackdrop':
      return (
        <DashboardBackdrop key={id} {...component.options}>
          {hasArrayValues(children) && children.map((child: BuilderElement) => getComponent(child))}
        </DashboardBackdrop>
      );
    case 'iframe':
      return <IFrame key={id} {...component.options} />;
    case 'JumpNav':
      return <JumpNav key={id} {...component.options} />;
    case 'LandingPageHero':
      return <LandingPageHero key={id} {...component.options} />;
    case 'MeetTheSpeakers':
      return <MeetTheSpeakers key={id} {...component.options} />;
    case 'Listing':
      return (
        <Listing
          key={id}
          {...component.options}
          builderState={data ? { state: data } : component.options.builderState}
        />
      );
    case 'metrics':
      return <Metrics key={id} {...component.options} />;
    // @deprecated - needs to be removed after migration of content to new pricing table `v2: Pricing Table`
    case 'Pricing Table':
      return <PricingV1 key={id} {...component.options} />;
    case 'v2: Pricing Table':
      return <PricingV2 key={id} {...component.options} />;
    case 'quoteCard':
      return <Quote key={id} {...component.options} />;
    case 'ResourceCardDeck':
      return <Resource key={id} {...component.options} />;
    case 'ResourceConversionPanel':
      return <ResourceConversionPanel key={id} {...component.options} />;
    case 'resourceDownloadCard':
      return <ResourceDownload key={id} {...component.options} />;
    case 'richText':
      return <RichText key={id} {...component.options} />;
    case 'image':
      return <RichTextImage key={id} {...component.options} />;
    case 'SimpleCardDeck':
      return <Simple key={id} {...component.options} />;
    case 'slidebox':
      return <Slidebox key={id} {...component.options} />;
    case 'SpeakerCards':
      return <SpeakerCards key={id} {...component.options} />;
    case 'socialShare':
      return <SocialShare key={id} {...component.options} />;
    case 'statsCard':
      return <Stats key={id} {...component.options} />;
    case 'StatsModule':
      return <StatsModule key={id} {...component.options} />;
    case 'SplitContentSection':
      return <SplitContentSection key={id} {...component.options} />;
    case 'switchback':
      return <Switchback key={id} {...component.options} />;
    case 'testimonials':
      return <Testimonials key={id} {...component.options} />;
    case 'Transition':
      return <Transition key={id} {...component.options} />;
    case 'trustbar':
      return <Trustbar key={id} {...component.options} />;
    case 'Video':
      return <Video key={id} {...component.options} />;
    case 'VideoCustom':
      return <VideoCustom key={id} {...component.options} />;
    case 'WistiaForm':
      return <WistiaForm key={id} {...component.options} />;
    case 'WistiaSplit':
      return <WistiaSplit key={id} {...component.options} />;
    case 'Symbol': {
      const symbols = component?.options?.symbol?.content?.data?.blocks;

      return hasArrayValues(symbols) && symbols.map((symbol: BuilderElement) => getComponent(symbol));
    }
    default:
      console.warn(`[ComponentGenerator] Unrecognized component: "${component?.name}"`);

      return null;
  }
};

const StaticComponentGenerator: FC<ComponentGeneratorProps> = ({ components, data }) => (
  <>{hasArrayValues(components) && components.map((component: BuilderElement) => getComponent(component, data))}</>
);

export default StaticComponentGenerator;
